import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { ALL_PARTY_ID } from '../../services/api/activePartyService';

const selectActivePartyState = (state: RootState) => state.activeParty;

export const selectActiveBuyer    = createSelector(selectActivePartyState, (s) => s.activeBuyer);
export const selectActiveSupplier = createSelector(selectActivePartyState, (s) => s.activeSupplier);

/** True when the axis is the explicit "All" sentinel (vs a specific party or null). */
export const selectActiveBuyerIsAll    = createSelector(selectActiveBuyer, (b) => b?.partyId === ALL_PARTY_ID);
export const selectActiveSupplierIsAll = createSelector(selectActiveSupplier, (s) => s?.partyId === ALL_PARTY_ID);

/** Party id for queries — null for both "unresolved" AND "All" (an "All" axis
 *  sends no party id; the authorized axis / global EDI access bounds the result). */
export const selectActiveBuyerPartyId    = createSelector(selectActiveBuyer, (b) => (b && b.partyId > 0 ? b.partyId : null));
export const selectActiveSupplierPartyId = createSelector(selectActiveSupplier, (s) => (s && s.partyId > 0 ? s.partyId : null));

/** Party codes — consumed by the Pack Analysis workbench pre-fill (Req 7). Null for "All". */
export const selectActiveBuyerPartyCode    = createSelector(selectActiveBuyer, (b) => (b && b.partyId > 0 ? b.partyCode : null));
export const selectActiveSupplierPartyCode = createSelector(selectActiveSupplier, (s) => (s && s.partyId > 0 ? s.partyCode : null));

/** My EDI scope (spec: edi-admin-and-scoped-party-selector) — drives the selector. */
export const selectMyEdiDirection      = createSelector(selectActivePartyState, (s) => s.direction);
export const selectMyEdiHasGlobalRead  = createSelector(selectActivePartyState, (s) => s.hasGlobalEdiRead);
export const selectMyBuyerParties      = createSelector(selectActivePartyState, (s) => s.myBuyerParties);
export const selectMySupplierParties   = createSelector(selectActivePartyState, (s) => s.mySupplierParties);

export const selectMyBuyers            = createSelector(selectActivePartyState, (s) => s.buyers);
export const selectActivePartyIsUnrestricted = createSelector(selectActivePartyState, (s) => s.isUnrestricted);
export const selectActivePartyAffiliationType = createSelector(selectActivePartyState, (s) => s.affiliationType);

export const selectActivePartyLoadedForTenant = createSelector(selectActivePartyState, (s) => s.loadedForTenantId);
export const selectActivePartyIsLoadingBuyers = createSelector(selectActivePartyState, (s) => s.isLoadingBuyers);
export const selectActivePartyIsLoadingSuppliers = createSelector(selectActivePartyState, (s) => s.isLoadingSuppliers);
export const selectActivePartyError = createSelector(selectActivePartyState, (s) => s.error);

/** The cached supplier list for the currently-active buyer (empty until fetched). */
export const selectSuppliersForActiveBuyer = createSelector(
  selectActivePartyState,
  (s) => (s.activeBuyer ? s.suppliersByBuyerId[s.activeBuyer.partyId] ?? [] : [])
);
